import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import type {
  BillToAddressSnapshot,
  ShipToAddressSnapshot,
} from '@/services/equipment-intake.service';
import type { AddressPreview } from './address-preview';
import { ADDRESS_SIDES, showsGst, type AddressSide } from './address-side';

/**
 * The address card — live before save, frozen after, identical either way.
 *
 * ONE renderer on purpose: the card the operator reads while choosing must be the
 * same card they read on the saved intake, or the preview stops being a preview.
 *
 * The body is plain text with no icons. Emphasis is spent on the side identity
 * and the place name only; an address where six fields all compete reads slower
 * than one where nothing does.
 */

/** Everything this card can render. Previews and frozen snapshots share a shape. */
type Renderable = AddressPreview | BillToAddressSnapshot | ShipToAddressSnapshot;

export function AddressCard({
  side,
  address,
  onEdit,
  canEdit = true,
}: {
  side: AddressSide;
  /** `undefined` = caller lacks intake:billing:view; `null` = nothing recorded. */
  address: Renderable | null | undefined;
  onEdit?: () => void;
  canEdit?: boolean;
}) {
  // Render nothing at all rather than an empty box that reveals billing data exists.
  if (address === undefined) return null;

  const s = ADDRESS_SIDES[side];

  return (
    <div className={`mt-1.5 overflow-hidden rounded-lg border ${s.border} bg-card`}>
      <div
        className={`flex items-center gap-2 border-b ${s.border} ${s.headerBg} px-3 py-1 ${s.text}`}
      >
        <FontAwesomeIcon icon={s.icon} className="h-3.5 w-3.5" />
        <span className="text-xs font-bold">{side}</span>
        {canEdit && onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className={`ml-auto inline-flex h-[30px] items-center gap-1.5 rounded-md border ${s.border} px-2.5 text-xs font-semibold ${s.text} transition-colors hover:bg-muted`}
          >
            <FontAwesomeIcon icon={faPenToSquare} className="h-3 w-3" />
            Edit
          </button>
        )}
      </div>

      <div className="px-3 pb-3 pt-2.5">
        {address ? (
          <AddressBody side={side} address={address} />
        ) : (
          <p className="text-sm text-muted-foreground">
            Not recorded — this intake predates Bill-To / Ship-To capture.
          </p>
        )}
      </div>
    </div>
  );
}

function AddressBody({ side, address }: { side: AddressSide; address: Renderable }) {
  const region = [address.city, address.state, address.postalCode].filter(Boolean).join(', ');
  const gst = showsGst(side) && 'gstNumber' in address ? address.gstNumber : null;

  const lines = [address.line1, address.line2, region, address.country, address.phone].filter(
    Boolean,
  );

  return (
    <>
      <p className="text-[15px] font-bold leading-snug text-foreground">{address.label}</p>
      <div className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">
        {address.line1 === null ? (
          // The server rejects an empty address line, so say so here rather than
          // letting Save fail with a 400 the operator cannot act on.
          <p className="text-amber-600 dark:text-amber-500">
            No street address on record — add one before saving.
          </p>
        ) : (
          lines.map((l, i) => <p key={i}>{l}</p>)
        )}
        {gst && <p className="mt-1.5">GSTIN: {gst}</p>}
      </div>
    </>
  );
}
